import React from 'react';
import { Moon, Star, PenTool, Layout, ArrowRight, BedDouble, Lamp, Palette } from 'lucide-react';
import { Link } from 'react-router-dom'; // Added for navigation
import img1 from "../../assets/bedroom1.jpg";
import img2 from "../../assets/bedroom2.jpg";
import img3 from "../../assets/bedroom3.jpg";
import img4 from "../../assets/bedroom4.jpg";
import img5 from "../../assets/bedroom5.jpg";
import img6 from "../../assets/bedroom6.jpg";
import img7 from "../../assets/bedroom7.jpg";
import img8 from "../../assets/bedroom8.jpg";
import img9 from "../../assets/bedroom9.jpg";
import img10 from "../../assets/bedroom10.jpg";

const popularDesigns = [
  { id: 1, title: 'Modern Minimalist', desc: 'Clean lines with neutral tones.', img: img1},
  { id: 2, title: 'Contemporary Luxe', desc: 'Gold accents & velvet textures.', img: img2 },
  { id: 3, title: 'Scandinavian', desc: 'Warm oak wood & white spaces.', img: img3},
  { id: 4, title: 'Industrial Chic', desc: 'Raw concrete & metal finishes.', img: img4 },
  { id: 5, title: 'Bohemian Rhapsody', desc: 'Earthy tones & artistic decor.', img: img5 },
  { id: 6, title: 'Classic Victorian', desc: 'Rich moldings & chandeliers.', img: img6},
  { id: 7, title: 'Moody Monotone', desc: 'Dark greys and bold lighting.', img: img7 },
  { id: 8, title: 'Kids Playland', desc: 'Bunk beds & vibrant themes.', img: img8 },
  { id: 9, title: 'Zen Retreat', desc: 'Low beds & Japanese aesthetics.', img: img9 },
];

const Bedroom = () => {
  return (
    <div className="bg-white font-sans text-neutral-900">
      
      {/* 1. HERO SECTION - Targeted for Comfort & Luxury */}
      <div
        className="relative h-[60vh] w-full bg-cover bg-center flex items-center justify-center text-center bg-neutral-900"
        style={{ backgroundImage: `url(${img10})` }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/30"></div>
        
        <div className="relative z-10 text-center px-6 animate-fadeIn mt-10">
          <span className="text-amber-400 tracking-[0.3em] text-xs font-bold uppercase mb-4 block">
            Sleep in Royalty
          </span>
          <h1 className="text-4xl md:text-6xl font-bold font-serif text-white mb-6">
            Luxury Bedroom Interiors
          </h1>
          <p className="text-gray-200 text-lg max-w-2xl mx-auto leading-relaxed font-light">
            Designing personal sanctuaries in <strong>Delhi, Lucknow & Varanasi</strong>. <br className="hidden md:block"/>
            Where comfort meets timeless elegance.
          </p>
        </div>
      </div>

      {/* 2. BEDROOM TYPES - 3 Column Feature */}
      <div className="py-20 px-6 max-w-7xl mx-auto border-b border-gray-100">
         <div className="text-center mb-16">
            <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-2">Categories</h4>
            <h2 className="text-3xl font-serif text-neutral-900">Spaces We Curate</h2>
         </div>
         
         <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
               { icon: <BedDouble size={40}/>, title: "Master Bedroom", desc: "A grand suite with walk-in closets and ambient lighting." },
               { icon: <Star size={40}/>, title: "Kids Room", desc: "Safe, fun, and creative spaces that grow with your child." },
               { icon: <Layout size={40}/>, title: "Guest Room", desc: "Welcoming comfort that makes guests feel at home." }
            ].map((item, idx) => (
               <div key={idx} className="text-center p-8 bg-neutral-50 hover:bg-white hover:shadow-xl transition-all duration-300 group rounded-sm border border-transparent hover:border-amber-100">
                  <div className="text-gray-400 group-hover:text-amber-600 mb-6 flex justify-center transition-colors">{item.icon}</div>
                  <h3 className="text-xl font-serif font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
               </div>
            ))}
         </div>
      </div>

      {/* 3. POPULAR DESIGNS - SEO Optimized Gallery */}
      <div className="py-24 px-6 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12">
           <div>
             <h4 className="text-amber-600 font-bold text-xs uppercase tracking-widest mb-2">Our Portfolio</h4>
             <h2 className="text-3xl md:text-4xl font-serif text-neutral-900">Popular Bedroom Themes</h2>
           </div>
           <Link to="/projects" className="hidden md:flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:text-amber-600 transition-colors">
              View All Projects <ArrowRight size={16}/>
           </Link>
        </div>

        {/* 3 Rows of Images Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {popularDesigns.map((design) => (
            <div key={design.id} className="group cursor-pointer">
              
              {/* Image Container */}
              <div className="relative overflow-hidden h-[350px] mb-[-40px] z-0 shadow-md">
                 <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all duration-500"></div>
                 <img 
                   src={design.img} 
                   alt={`${design.title} Bedroom Design Idea`} // SEO Alt Text
                   loading="lazy"
                   className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000"
                 />
              </div>

              {/* The "Half White Card" - Floating Effect */}
              <div className="relative z-10 w-[90%] mx-auto bg-white p-6 shadow-xl border-t-4 border-transparent group-hover:border-amber-600 transition-all duration-300">
                 <h3 className="text-lg font-serif font-bold text-neutral-900 mb-1 group-hover:text-amber-700 transition-colors">
                   {design.title}
                 </h3>
                 <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">{design.desc}</p>
                    <ArrowRight size={14} className="text-amber-600 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300"/>
                 </div>
              </div>

            </div>
          ))}
        </div>
        
        {/* Mobile View All Button */}
        <div className="mt-12 text-center md:hidden">
           <Link to="/projects" className="bg-white border border-neutral-900 px-8 py-3 text-xs font-bold uppercase tracking-widest">
              Load More Designs
           </Link>
        </div>
      </div>

      {/* 4. KEY ELEMENTS SECTION */}
      <div className="bg-neutral-900 text-white py-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
           <div className="text-center mb-16">
              <h2 className="text-3xl font-serif text-white">Elements of a Royal Bedroom</h2>
              <p className="text-gray-400 mt-2">Essential features for homes in Noida & UP.</p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="flex gap-6 items-start p-6 hover:bg-white/5 rounded-lg transition-colors">
                 <div className="bg-amber-600/20 p-4 rounded-full text-amber-500">
                    <Palette size={24} />
                 </div>
                 <div>
                    <h3 className="text-xl font-serif font-bold mb-2">Signature Headboards</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                       From tufted velvet (Lucknow style) to handcrafted wooden panels, the headboard is the crown of your bed.
                    </p>
                 </div>
              </div>

              {/* Feature 2 */}
              <div className="flex gap-6 items-start p-6 hover:bg-white/5 rounded-lg transition-colors">
                 <div className="bg-amber-600/20 p-4 rounded-full text-amber-500">
                    <Layout size={24} />
                 </div>
                 <div>
                    <h3 className="text-xl font-serif font-bold mb-2">Smart Wardrobes</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                       Floor-to-ceiling sliding wardrobes with automated lighting and custom organizers for maximum storage.
                    </p>
                 </div>
              </div>

              {/* Feature 3 */}
              <div className="flex gap-6 items-start p-6 hover:bg-white/5 rounded-lg transition-colors">
                 <div className="bg-amber-600/20 p-4 rounded-full text-amber-500">
                    <Lamp size={24} />
                 </div>
                 <div>
                    <h3 className="text-xl font-serif font-bold mb-2">Mood Lighting</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                       Warm cove lights and designer pendants to switch from 'Work Mode' to 'Sleep Mode'.
                    </p>
                 </div>
              </div>
           </div>
        </div>
      </div>

      {/* 5. CTA - Location Specific */}
      <div className="py-20 text-center bg-amber-50">
         <h2 className="text-3xl font-serif mb-4 text-neutral-900">Wake Up in Your Dream Room</h2>
         <p className="text-gray-500 mb-8">Schedule a bedroom consultation in <strong>Delhi, Lucknow or Varanasi</strong>.</p>
         <Link to="/contact" className="inline-block bg-neutral-900 text-white px-10 py-4 text-sm font-bold tracking-widest uppercase hover:bg-amber-600 transition-all shadow-xl">
            Get a Quote
         </Link>
      </div>

    </div>
  );
};

export default Bedroom;